import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Building2,
  FileText,
  Loader2,
  LogIn,
  MessageSquare,
  UserCircle,
  UserPlus,
} from 'lucide-react';

import {
  createOnboardingSession,
  getOnboardingSession,
  updateOnboardingSession,
  type OnboardingSessionRecord,
} from '@/lib/onboarding';
import {
  Callout,
  cn,
  InspectorSection,
  PropertyGrid,
  PropertyItem,
  SectionEyebrow,
  SilkInput,
  SilkSelect,
  VerticalStack,
  WizardWorkspace,
  type WizardTab,
} from '@workspace/silk-ui';

// ── Options ───────────────────────────────────────────────────────────────────

const EMPLOYEE_COUNT_OPTIONS = [
  { value: '1-10', label: '1 – 10 employees' },
  { value: '11-50', label: '11 – 50 employees' },
  { value: '51-250', label: '51 – 250 employees' },
  { value: '251-1000', label: '251 – 1,000 employees' },
  { value: '1000+', label: '1,000+ employees' },
];

const COMPANY_TYPE_OPTIONS = [
  { value: 'telecom_reseller', label: 'Telecom reseller / agency' },
  { value: 'telecom_operator', label: 'Telecom operator / carrier' },
  { value: 'systems_integrator', label: 'Systems integrator' },
  { value: 'enterprise_buyer', label: 'Enterprise buyer / internal team' },
  { value: 'other', label: 'Other' },
];

const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'IE', label: 'Ireland' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'SE', label: 'Sweden' },
  { value: 'NO', label: 'Norway' },
  { value: 'DK', label: 'Denmark' },
  { value: 'FI', label: 'Finland' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'AT', label: 'Austria' },
  { value: 'BE', label: 'Belgium' },
  { value: 'ES', label: 'Spain' },
  { value: 'IT', label: 'Italy' },
  { value: 'PT', label: 'Portugal' },
  { value: 'PL', label: 'Poland' },
  { value: 'CZ', label: 'Czechia' },
  { value: 'HU', label: 'Hungary' },
  { value: 'RO', label: 'Romania' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'SG', label: 'Singapore' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'IN', label: 'India' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'TH', label: 'Thailand' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'PH', label: 'Philippines' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'QA', label: 'Qatar' },
  { value: 'IL', label: 'Israel' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'KE', label: 'Kenya' },
  { value: 'EG', label: 'Egypt' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'AR', label: 'Argentina' },
  { value: 'CL', label: 'Chile' },
  { value: 'CO', label: 'Colombia' },
];

// ── Wizard steps ──────────────────────────────────────────────────────────────

type WizardStep = 'company' | 'contact' | 'platform' | 'review';

const WIZARD_TABS: WizardTab[] = [
  { id: 'company', label: 'Company', icon: <Building2 className="h-4 w-4" /> },
  { id: 'contact', label: 'Contact', icon: <UserCircle className="h-4 w-4" /> },
  { id: 'platform', label: 'Platform use', icon: <MessageSquare className="h-4 w-4" /> },
  { id: 'review', label: 'Review & submit', icon: <FileText className="h-4 w-4" /> },
];

// ── Types ─────────────────────────────────────────────────────────────────────

interface PortalStartPageProps {
  path: string;
  login: (returnUrl?: string, options?: { register?: boolean }) => void;
}

interface StartDraftState {
  companyName: string;
  contactEmail: string;
  contactName: string;
  countryCode: string;
  employeeCountBand: string;
  companyType: string;
  intendedUse: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function emptyDraft(): StartDraftState {
  return {
    companyName: '',
    contactEmail: '',
    contactName: '',
    countryCode: 'US',
    employeeCountBand: '',
    companyType: '',
    intendedUse: '',
  };
}

function draftToApplicationDraft(state: StartDraftState) {
  return {
    companyName: state.companyName.trim(),
    contactEmail: state.contactEmail.trim(),
    contactName: state.contactName.trim(),
    countryCode: state.countryCode.trim().toUpperCase(),
    employeeCountBand: state.employeeCountBand,
    companyType: state.companyType,
    intendedUse: state.intendedUse.trim(),
  };
}

function applySessionDraft(session: OnboardingSessionRecord | null): StartDraftState {
  const raw = session?.applicationDraft ?? {};
  return {
    companyName: typeof raw.companyName === 'string' ? raw.companyName : '',
    contactEmail: typeof raw.contactEmail === 'string' ? raw.contactEmail : '',
    contactName: typeof raw.contactName === 'string' ? raw.contactName : '',
    countryCode: typeof raw.countryCode === 'string' && raw.countryCode ? raw.countryCode : 'US',
    employeeCountBand: typeof raw.employeeCountBand === 'string' ? raw.employeeCountBand : '',
    companyType: typeof raw.companyType === 'string' ? raw.companyType : '',
    intendedUse: typeof raw.intendedUse === 'string' ? raw.intendedUse : '',
  };
}

function validateStep(step: WizardStep, draft: StartDraftState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (step === 'company') {
    if (!draft.companyName.trim()) errors.companyName = 'Company name is required.';
    if (!draft.companyType) errors.companyType = 'Select a company type.';
    if (!draft.employeeCountBand) errors.employeeCountBand = 'Select a team size.';
  }
  if (step === 'contact') {
    if (!draft.contactName.trim()) errors.contactName = 'Contact name is required.';
    if (!draft.contactEmail.trim()) {
      errors.contactEmail = 'Contact email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.contactEmail.trim())) {
      errors.contactEmail = 'Enter a valid email address.';
    }
  }
  return errors;
}

function validateAll(draft: StartDraftState): Record<string, string> {
  return { ...validateStep('company', draft), ...validateStep('contact', draft) };
}

// ── Small UI helpers ──────────────────────────────────────────────────────────

function FieldError({ error }: { error?: string }) {
  if (!error) return null;
  return (
    <p className="flex items-center gap-1 text-xs text-destructive">
      <AlertCircle className="h-3 w-3 shrink-0" />
      {error}
    </p>
  );
}

function StepHeading({ step, total, title, description }: {
  step: number;
  total: number;
  title: string;
  description: string;
}) {
  return (
    <div className="space-y-1">
      <SectionEyebrow>Step {step} of {total}</SectionEyebrow>
      <h2 className="text-xl font-bold text-foreground">{title}</h2>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function PortalStartPage({ path, login }: PortalStartPageProps) {
  const search = useMemo(
    () => (path.includes('?') ? path.slice(path.indexOf('?') + 1) : ''),
    [path],
  );
  const onboardingSessionId = useMemo(
    () => new URLSearchParams(search).get('onboarding_session')?.trim() ?? '',
    [search],
  );

  const [draft, setDraft] = useState<StartDraftState>(emptyDraft);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [activeTabId, setActiveTabId] = useState<WizardStep>('company');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!onboardingSessionId) {
      setDraft(emptyDraft());
      return;
    }
    let cancelled = false;
    setLoading(true);
    void getOnboardingSession(onboardingSessionId)
      .then(session => {
        if (cancelled) return;
        if (session) setDraft(applySessionDraft(session));
      })
      .catch(loadError => {
        if (!cancelled)
          setError(loadError instanceof Error ? loadError.message : 'Failed to load your saved draft.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [onboardingSessionId]);

  const handleFieldChange = (field: keyof StartDraftState, value: string) => {
    setError(null);
    setNotice(null);
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setDraft(prev => ({ ...prev, [field]: value }));
  };

  const ensureSession = async () => {
    const payload = {
      status: 'awaiting_auth',
      intent: 'create_company',
      applicationDraft: draftToApplicationDraft(draft),
      metadata: null,
    };
    if (onboardingSessionId) {
      return (await updateOnboardingSession(onboardingSessionId, payload)).id;
    }
    return (await createOnboardingSession(payload)).id;
  };

  const handleSaveDraft = async () => {
    const allErrors = validateAll(draft);
    if (Object.keys(allErrors).length > 0) {
      setFieldErrors(allErrors);
      setActiveTabId('company');
      setError('Complete the required fields on steps 1 and 2 to save your draft.');
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await ensureSession();
      setNotice('Draft saved. Return to this page at any time to resume your registration.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save your draft.');
    } finally {
      setSaving(false);
    }
  };

  const beginAuth = async (register: boolean) => {
    const allErrors = validateAll(draft);
    if (Object.keys(allErrors).length > 0) {
      setFieldErrors(allErrors);
      setActiveTabId('company');
      setError('Please complete all required fields before continuing.');
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const sessionId = await ensureSession();
      const returnUrl = `${window.location.origin}/?onboarding_session=${encodeURIComponent(sessionId)}`;
      login(returnUrl, { register });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start onboarding.');
      setSaving(false);
    }
  };

  const handleTabChange = (id: string) => {
    const currentIndex = WIZARD_TABS.findIndex(t => t.id === activeTabId);
    const targetIndex = WIZARD_TABS.findIndex(t => t.id === id);
    // Validate on forward navigation only
    if (targetIndex > currentIndex) {
      const errors = validateStep(activeTabId as WizardStep, draft);
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
    }
    setFieldErrors({});
    setError(null);
    setNotice(null);
    setActiveTabId(id as WizardStep);
  };

  const disabled = loading || saving;

  const summaryItems = useMemo(
    () => [
      {
        label: 'Company',
        value: draft.companyName.trim() || '—',
        complete: !!draft.companyName.trim(),
      },
      {
        label: 'Type',
        value: COMPANY_TYPE_OPTIONS.find(o => o.value === draft.companyType)?.label ?? '—',
        complete: !!draft.companyType,
      },
      {
        label: 'Team size',
        value: EMPLOYEE_COUNT_OPTIONS.find(o => o.value === draft.employeeCountBand)?.label ?? '—',
        complete: !!draft.employeeCountBand,
      },
      {
        label: 'Contact',
        value: draft.contactName.trim() || '—',
        complete: !!draft.contactName.trim(),
      },
      {
        label: 'Email',
        value: draft.contactEmail.trim() || '—',
        complete:
          !!draft.contactEmail.trim() &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.contactEmail.trim()),
      },
    ],
    [draft],
  );

  const allComplete = summaryItems.every(i => i.complete);

  // ── Input class helper ─────────────────────────────────────────────────────
  const inputCls = (field: string) =>
    cn('h-9 text-sm', fieldErrors[field] && 'border-destructive ring-1 ring-destructive/30');

  return (
    <div className="h-screen overflow-hidden bg-background">
      <WizardWorkspace
        isOpen={true}
        title="Company registration"
        subtitle="Request platform access for your organisation"
        tabs={WIZARD_TABS}
        activeTabId={activeTabId}
        onTabChange={handleTabChange}
        saveLabel={saving ? 'Saving…' : 'Save draft'}
        onSave={() => void handleSaveDraft()}
        isSaving={saving}
        isLoading={loading}
        onClose={() => window.location.assign(window.location.origin)}
        discardLabel="Exit"
        summaryEyebrow="Application progress"
        summaryItems={summaryItems}
        summaryCallout={
          !allComplete ? 'Fill in all required fields to submit your application.' : undefined
        }
        maxContentWidth="max-w-xl"
      >
        {/* ── Step 1: Company ────────────────────────────────────────────── */}
        {activeTabId === 'company' && (
          <VerticalStack gap={6}>
            <StepHeading
              step={1}
              total={4}
              title="About your company"
              description={`Tell us about your organisation. Fields marked \u002a are required.`}
            />

            {error && <Callout variant="error">{error}</Callout>}
            {loading && (
              <Callout variant="info">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading your saved draft…
                </span>
              </Callout>
            )}

            <VerticalStack gap={4}>
              <div className="space-y-1.5">
                <label htmlFor="start-company-name" className="text-xs font-medium text-foreground">
                  Company name <span className="text-destructive">*</span>
                </label>
                <SilkInput
                  id="start-company-name"
                  value={draft.companyName}
                  onChange={e => handleFieldChange('companyName', e.target.value)}
                  placeholder="Acme Telecom Partners"
                  disabled={disabled}
                  className={inputCls('companyName')}
                />
                <FieldError error={fieldErrors.companyName} />
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="start-company-type"
                  className="text-xs font-medium text-foreground"
                >
                  Company type <span className="text-destructive">*</span>
                </label>
                <SilkSelect
                  id="start-company-type"
                  value={draft.companyType}
                  onValueChange={v => handleFieldChange('companyType', v)}
                  disabled={disabled}
                  className={cn('h-9 w-full text-sm', fieldErrors.companyType && 'border-destructive ring-1 ring-destructive/30')}
                >
                  <option value="" disabled>
                    Select type…
                  </option>
                  {COMPANY_TYPE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </SilkSelect>
                <FieldError error={fieldErrors.companyType} />
                <p className="text-[11px] text-muted-foreground/70">
                  Choose the category that best describes your organisation.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label
                    htmlFor="start-employee-count"
                    className="text-xs font-medium text-foreground"
                  >
                    Team size <span className="text-destructive">*</span>
                  </label>
                  <SilkSelect
                    id="start-employee-count"
                    value={draft.employeeCountBand}
                    onValueChange={v => handleFieldChange('employeeCountBand', v)}
                    disabled={disabled}
                    className={cn('h-9 w-full text-sm', fieldErrors.employeeCountBand && 'border-destructive ring-1 ring-destructive/30')}
                  >
                    <option value="" disabled>
                      Select range…
                    </option>
                    {EMPLOYEE_COUNT_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </SilkSelect>
                  <FieldError error={fieldErrors.employeeCountBand} />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="start-country-code"
                    className="text-xs font-medium text-foreground"
                  >
                    Country{' '}
                    <span className="text-[10px] font-normal text-muted-foreground/60">
                      (optional)
                    </span>
                  </label>
                  <SilkSelect
                    id="start-country-code"
                    value={draft.countryCode}
                    onValueChange={v => handleFieldChange('countryCode', v)}
                    disabled={disabled}
                    className="h-9 w-full text-sm"
                  >
                    {COUNTRY_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </SilkSelect>
                </div>
              </div>
            </VerticalStack>
          </VerticalStack>
        )}

        {/* ── Step 2: Contact ─────────────────────────────────────────────── */}
        {activeTabId === 'contact' && (
          <VerticalStack gap={6}>
            <StepHeading
              step={2}
              total={4}
              title="Your contact details"
              description="Who should we reach if we need to discuss your application?"
            />

            {error && <Callout variant="error">{error}</Callout>}

            <VerticalStack gap={4}>
              <div className="space-y-1.5">
                <label
                  htmlFor="start-contact-name"
                  className="text-xs font-medium text-foreground"
                >
                  Full name <span className="text-destructive">*</span>
                </label>
                <SilkInput
                  id="start-contact-name"
                  value={draft.contactName}
                  onChange={e => handleFieldChange('contactName', e.target.value)}
                  placeholder="Avery Customer"
                  disabled={disabled}
                  className={inputCls('contactName')}
                />
                <FieldError error={fieldErrors.contactName} />
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="start-contact-email"
                  className="text-xs font-medium text-foreground"
                >
                  Work email <span className="text-destructive">*</span>
                </label>
                <SilkInput
                  id="start-contact-email"
                  type="email"
                  value={draft.contactEmail}
                  onChange={e => handleFieldChange('contactEmail', e.target.value)}
                  placeholder="ops@example.com"
                  disabled={disabled}
                  className={inputCls('contactEmail')}
                />
                <FieldError error={fieldErrors.contactEmail} />
              </div>
            </VerticalStack>

            <Callout variant="info">
              Your email address is used for account verification and application status updates.
              If the verification message doesn&apos;t arrive immediately, you can return here and
              sign in again — your draft will still be on the server.
            </Callout>
          </VerticalStack>
        )}

        {/* ── Step 3: Platform use ─────────────────────────────────────────── */}
        {activeTabId === 'platform' && (
          <VerticalStack gap={6}>
            <StepHeading
              step={3}
              total={4}
              title="Platform use"
              description="This step is optional but helps our team configure the right features for your account."
            />

            {error && <Callout variant="error">{error}</Callout>}

            <div className="space-y-1.5">
              <label
                htmlFor="start-intended-use"
                className="text-xs font-medium text-foreground"
              >
                How will you use the platform?{' '}
                <span className="text-[10px] font-normal text-muted-foreground/60">(optional)</span>
              </label>
              <textarea
                id="start-intended-use"
                value={draft.intendedUse}
                onChange={e => handleFieldChange('intendedUse', e.target.value)}
                placeholder="We plan to use the platform to resell mobile services to enterprise customers across the Nordics…"
                rows={7}
                disabled={disabled}
                className={cn(
                  'w-full resize-y rounded border border-border/50 bg-background px-3 py-2.5 text-sm text-foreground',
                  'placeholder:text-muted-foreground/50 transition-colors',
                  'hover:border-border focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50',
                )}
              />
              <p className="text-[11px] leading-relaxed text-muted-foreground/70">
                The more detail you provide, the faster our team can review and approve your
                application.
              </p>
            </div>

            <Callout variant="info">
              Examples: reselling telecom services, receiving shared map access, building API
              integrations, managing enterprise accounts.
            </Callout>
          </VerticalStack>
        )}

        {/* ── Step 4: Review & submit ──────────────────────────────────────── */}
        {activeTabId === 'review' && (
          <VerticalStack gap={6}>
            <StepHeading
              step={4}
              total={4}
              title="Review & get started"
              description="Check your details, then create an account or sign in to submit your application."
            />

            {error && <Callout variant="error">{error}</Callout>}
            {notice && <Callout variant="success">{notice}</Callout>}

            {/* Application summary */}
            <div className="overflow-hidden rounded-xl border border-border/50 bg-muted/10">
              <InspectorSection title="Company" className="border-b border-border/40 p-5">
                <PropertyGrid cols={2}>
                  <PropertyItem
                    label="Name"
                    value={draft.companyName.trim() || '—'}
                    mono={false}
                  />
                  <PropertyItem
                    label="Country"
                    value={
                      COUNTRY_OPTIONS.find(o => o.value === draft.countryCode)?.label ??
                      draft.countryCode
                    }
                    mono={false}
                  />
                  <PropertyItem
                    label="Type"
                    value={
                      COMPANY_TYPE_OPTIONS.find(o => o.value === draft.companyType)?.label ?? '—'
                    }
                    mono={false}
                  />
                  <PropertyItem
                    label="Team size"
                    value={
                      EMPLOYEE_COUNT_OPTIONS.find(o => o.value === draft.employeeCountBand)
                        ?.label ?? '—'
                    }
                    mono={false}
                  />
                </PropertyGrid>
              </InspectorSection>

              <InspectorSection title="Contact" className="border-b border-border/40 p-5">
                <PropertyGrid cols={2}>
                  <PropertyItem
                    label="Name"
                    value={draft.contactName.trim() || '—'}
                    mono={false}
                  />
                  <PropertyItem
                    label="Email"
                    value={draft.contactEmail.trim() || '—'}
                    mono={false}
                  />
                </PropertyGrid>
              </InspectorSection>

              {draft.intendedUse.trim() ? (
                <InspectorSection title="Platform use" className="p-5">
                  <p className="whitespace-pre-line text-xs leading-relaxed text-foreground">
                    {draft.intendedUse.trim()}
                  </p>
                </InspectorSection>
              ) : (
                <div className="border-t border-border/40 px-5 py-3">
                  <p className="text-xs italic text-muted-foreground/50">
                    No platform use description provided.
                  </p>
                </div>
              )}
            </div>

            {!allComplete && (
              <Callout variant="warning">
                Required fields are incomplete. Go back and fill in all fields marked{' '}
                <strong className="font-semibold">*</strong> before submitting.
              </Callout>
            )}

            {/* Auth CTAs */}
            <VerticalStack gap={2}>
              <button
                type="button"
                disabled={disabled || !allComplete}
                onClick={() => void beginAuth(true)}
                className={cn(
                  'flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3.5',
                  'text-sm font-bold shadow-sm transition-all',
                  'bg-primary text-primary-foreground hover:opacity-90 active:scale-[0.99]',
                  'disabled:pointer-events-none disabled:opacity-50',
                )}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4" />
                )}
                {saving ? 'Starting…' : 'Create account'}
              </button>

              <button
                type="button"
                disabled={disabled || !allComplete}
                onClick={() => void beginAuth(false)}
                className={cn(
                  'flex w-full items-center justify-center gap-2 rounded-lg border border-border/60 px-6 py-3.5',
                  'text-sm font-bold transition-all',
                  'bg-background text-foreground hover:bg-muted/50',
                  'disabled:pointer-events-none disabled:opacity-50',
                )}
              >
                <LogIn className="h-4 w-4" />
                Sign in to continue
              </button>
            </VerticalStack>

            <Callout variant="info">
              Your draft is saved on the server — you can stop here and resume later. Verification
              and company approval do not need to happen in a single session.
            </Callout>
          </VerticalStack>
        )}
      </WizardWorkspace>
    </div>
  );
}
